import { useState, type ReactNode } from 'react';
import type { SsoRevokeScope, SsoSession } from '../types/index';

type Facility = SsoSession['facilities'][number];

type Props = {
  sessions: SsoSession[];
  busy?: boolean;
  onRevokeAll: () => void | Promise<void>;
  // One organization/market sign-in, or one of its facilities (mcaFacilityId set).
  onRevokeSession: (scope: SsoRevokeScope) => void | Promise<void>;
  // Expired sign-ins (tokens cleared after Metrc rejected them) offer a new sign-in instead of Revoke.
  onSignIn?: (session: SsoSession) => void | Promise<void>;
};

type Pending =
  | { kind: 'all' }
  | { kind: 'market'; session: SsoSession }
  | { kind: 'facility'; session: SsoSession; facility: Facility };

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const facilityLabel = (facility: Facility): string => {
  const name = facility.name?.trim() || `Facility ${facility.mcaFacilityId}`;
  return facility.license ? `${name} (${facility.license})` : name;
};

const lastUsedLabel = (session: SsoSession): string => {
  if (!session.lastUsedAt) return 'Never';
  const when = formatDateTime(session.lastUsedAt);
  if (session.lastUsedByYou == null) return when;
  return `${when} by ${session.lastUsedByYou ? 'you' : 'another user'}`;
};

const STATUS_LABELS: Record<SsoSession['status'], string> = {
  active: 'Active',
  expiringSoon: 'Expiring soon',
  expired: 'Expired',
};

const pluralFacilities = (count: number): string => `${count} ${count === 1 ? 'facility' : 'facilities'}`;

const smallButton = { height: 28, padding: '0 10px', fontSize: 13 } as const;

type ConfirmProps = {
  busy: boolean;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  children: ReactNode;
};

// One confirmation is open at a time, so its test ids are shared by every scope.
function ConfirmBox({ busy, confirmLabel, onCancel, onConfirm, children }: ConfirmProps) {
  return (
    <div className="card" style={{ marginTop: 12 }} role="alertdialog" aria-label="Revoke Metrc sign-ins" data-testid="mca-sessions-confirm">
      {children}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
        <button type="button" className="button secondary" onClick={onCancel} disabled={busy} data-testid="mca-sessions-revoke-cancel">
          Cancel
        </button>
        <button type="button" className="button" onClick={onConfirm} disabled={busy} data-testid="mca-sessions-revoke-confirm">
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}

export default function McaSessionsPanel({ sessions, busy = false, onRevokeAll, onRevokeSession, onSignIn }: Props) {
  const [pending, setPending] = useState<Pending | null>(null);

  const facilityCount = sessions.reduce((total, session) => total + session.facilities.length, 0);
  const organizationCount = new Set(sessions.map((session) => session.organizationId)).size;
  const usedByOthers = sessions.some((session) => session.lastUsedByYou === false);

  const handleConfirm = async () => {
    if (!pending) return;
    if (pending.kind === 'all') {
      await onRevokeAll();
    } else {
      await onRevokeSession({
        organizationId: pending.session.organizationId,
        marketId: pending.session.marketId,
        ...(pending.kind === 'facility' ? { mcaFacilityId: pending.facility.mcaFacilityId } : {}),
      });
    }
    setPending(null);
  };

  const sharedWarning = (session: SsoSession) => (
    <>
      {session.lastUsedByYou === false && (
        <div style={{ marginTop: 8 }} data-testid="mca-sessions-impact-used">
          Other RetailId users have used this sign-in.
        </div>
      )}
    </>
  );

  return (
    <div className="card" data-testid="mca-sessions-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: 16 }}>Metrc sign-ins saved in your name</div>
          <div className="muted" style={{ marginTop: 4 }}>
            RetailId uses these to import facilities and products from Metrc. Other users in your organization can be served by them too.
          </div>
        </div>
        <button
          type="button"
          className="button secondary"
          onClick={() => setPending({ kind: 'all' })}
          disabled={busy || sessions.length === 0}
          data-testid="mca-sessions-revoke-all"
        >
          Revoke all
        </button>
      </div>

      {pending?.kind === 'all' && (
        <ConfirmBox busy={busy} confirmLabel="Revoke all" onCancel={() => setPending(null)} onConfirm={handleConfirm}>
          <div data-testid="mca-sessions-impact">
            RetailId will stop using your Metrc sign-ins: {pluralFacilities(facilityCount)} in {organizationCount}{' '}
            {organizationCount === 1 ? 'organization' : 'organizations'}.
          </div>
          {usedByOthers && (
            <div style={{ marginTop: 8 }} data-testid="mca-sessions-impact-used">
              Other RetailId users have used your sign-ins.
            </div>
          )}
          <div className="muted" style={{ marginTop: 8 }}>
            This removes them from RetailId. It does not sign you out of Metrc.
          </div>
        </ConfirmBox>
      )}

      {sessions.length === 0 ? (
        <div className="muted" style={{ marginTop: 12 }} data-testid="mca-sessions-empty">
          No Metrc sign-ins are saved for your account.
        </div>
      ) : (
        sessions.map((session) => {
          const marketPending = pending?.kind === 'market' && pending.session.id === session.id;
          const needsSignIn = session.status === 'expired' && Boolean(onSignIn);
          const signInButton = (testId: string) => (
            <button
              type="button"
              className="button"
              style={smallButton}
              onClick={() => onSignIn?.(session)}
              disabled={busy}
              data-testid={testId}
            >
              Sign in
            </button>
          );
          return (
            <section
              key={session.id}
              style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border)' }}
              data-testid={`mca-session-${session.id}`}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 700 }}>
                    {session.organizationName}
                  </div>
                  <div className="muted">{session.marketName} · {session.mcaEmail || '—'}</div>
                  <div className="muted" style={{ marginTop: 4 }}>
                    Signed in {formatDateTime(session.signedInAt)} · Last refreshed {formatDateTime(session.lastRefreshedAt)} ·{' '}
                    <span data-testid={`mca-session-${session.id}-status`}>
                      {STATUS_LABELS[session.status] || STATUS_LABELS.active}
                      {session.expiresAt && ` (${session.status === 'expired' ? 'expired' : 'expires'} ${formatDateTime(session.expiresAt)})`}
                    </span>{' '}
                    · Last used <span data-testid={`mca-session-${session.id}-last-used`}>{lastUsedLabel(session)}</span>
                  </div>
                </div>
                {needsSignIn ? signInButton(`mca-session-${session.id}-sign-in`) : (
                  <button
                    type="button"
                    className="button secondary"
                    style={smallButton}
                    onClick={() => setPending({ kind: 'market', session })}
                    disabled={busy}
                    data-testid={`mca-session-${session.id}-revoke`}
                  >
                    Revoke all in {session.marketName}
                  </button>
                )}
              </div>

              {marketPending && (
                <ConfirmBox busy={busy} confirmLabel="Revoke" onCancel={() => setPending(null)} onConfirm={handleConfirm}>
                  <div data-testid="mca-sessions-impact">
                    RetailId will stop using your Metrc sign-in for {session.organizationName} in {session.marketName}:{' '}
                    {pluralFacilities(session.facilities.length)}.
                  </div>
                  {sharedWarning(session)}
                  <div className="muted" style={{ marginTop: 8 }}>
                    This removes it from RetailId. It does not sign you out of Metrc.
                  </div>
                </ConfirmBox>
              )}

              <table className="table">
                <thead>
                  <tr>
                    <th>{pluralFacilities(session.facilities.length)}</th>
                    <th style={{ width: 1 }} />
                  </tr>
                </thead>
                <tbody>
                  {session.facilities.map((facility) => {
                    const facilityPending = pending?.kind === 'facility'
                      && pending.session.id === session.id
                      && pending.facility.mcaFacilityId === facility.mcaFacilityId;
                    return (
                      <tr key={facility.mcaFacilityId} data-testid={`mca-session-${session.id}-facility-${facility.mcaFacilityId}`}>
                        <td>
                          {facilityLabel(facility)}
                          {facilityPending && (
                            <ConfirmBox busy={busy} confirmLabel="Revoke" onCancel={() => setPending(null)} onConfirm={handleConfirm}>
                              <div data-testid="mca-sessions-impact">
                                RetailId will stop using your Metrc sign-in for {facilityLabel(facility)}. Your other facilities are not affected.
                              </div>
                              <div className="muted" style={{ marginTop: 8 }}>
                                Signing in with Metrc again saves every facility your account can access.
                              </div>
                            </ConfirmBox>
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                          {needsSignIn ? signInButton(`mca-session-${session.id}-facility-${facility.mcaFacilityId}-sign-in`) : (
                            <button
                              type="button"
                              className="button secondary"
                              style={smallButton}
                              onClick={() => setPending({ kind: 'facility', session, facility })}
                              disabled={busy}
                              data-testid={`mca-session-${session.id}-facility-${facility.mcaFacilityId}-revoke`}
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          );
        })
      )}
    </div>
  );
}
